import React, { useEffect, useState, useMemo } from 'react';
import Papa from 'papaparse';
import { format } from 'date-fns-tz';
import {
  Alert,
  Button,
  Drawer,
  FileField,
  Input,
  Container,
  H1,
  Loader,
  Table
} from '@itds/elements/index.js';
import { Colors } from '@itds/Theme';
import EditDrawer from './Edit';
import { Api } from '@carrier/utils/Api';
import Cookies from 'js-cookie';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAmbestRecords,
  setSearchQuery,
  setCurrentPage,
  clearCache
} from '@redux/carrierSlices/ambestSlice';
import { debounce } from 'lodash';

const List = () => {
  const userEmail = Cookies.get('email');
  const dispatch = useDispatch();
  const { ambestRecords, pagination, loading, error, currentPage, searchQuery } = useSelector(
    state => state.ambest
  );
  const [localSearch, setLocalSearch] = useState(searchQuery);
  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importErrors, setImportErrors] = useState([]);
  const [record, setRecord] = useState(null);
  const [carriers, setCarriers] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    Api.loadCarriers().then(({ data }) => {
      setCarriers(data);
    });
  }, []);

  useEffect(() => {
    dispatch(fetchAmbestRecords({ page: currentPage, search: searchQuery }));
  }, [dispatch, currentPage, searchQuery]);

  const debouncedSetSearchTerm = useMemo(
    () => debounce(value => dispatch(setSearchQuery(value)), 500),
    [dispatch]
  );

  const handleSearchChange = e => {
    const value = e.target.value.trimStart();

    setLocalSearch(value);
    debouncedSetSearchTerm(value);
  };

  const handleSearchKeyDown = e => {
    if (e.keyCode === 13) {
      debouncedSetSearchTerm.cancel();
      dispatch(setSearchQuery(localSearch));
      dispatch(setCurrentPage(1));
      dispatch(clearCache());
    }
  };

  const handlePageChange = page => {
    window.scrollTo(0, 0);
    dispatch(setCurrentPage(page));
    dispatch(fetchAmbestRecords({ page, search: searchQuery }));
  };

  const handleImportChange = file => {
    setImportFile(file);
    setImportErrors([]);

    if (file) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: results => {
          const errors = [];
          const requiredColumnHeaders = [];

          if (results.errors.length) {
            errors.push(
              'Could not parse the selected file. Please check the file format and try again.'
            );
            setImportErrors(errors);
          } else {
            requiredColumnHeaders.forEach(header => {
              if (!results.meta.fields.includes(header)) {
                errors.push(header);
              }
            });
            setImportErrors(errors);
          }
        }
      });
    }
  };

  const startImport = () => {
    const formData = new FormData();

    formData.append('file', importFile);
    formData.append('email', userEmail);

    Api.updateAmbestRecord(formData)
      .then(() => {
        handleSave('save');
        setShowImport(false);
        setSuccessMessage('AMBest Record Successfully Imported');
      })
      .catch(() => {
        setErrorMessage(
          'Could not import the selected file. Please check the file format and try again.'
        );
      });
  };

  const handleSave = action => {
    setRecord(null);
    dispatch(clearCache());
    dispatch(fetchAmbestRecords({ page: currentPage, search: searchQuery, forceRefresh: true }));
    if (action === 'save') {
      setSuccessMessage('AMBest Record Successfully Saved');
    }
  };

  return loading ? (
    <Loader />
  ) : (
    <Container>
      {successMessage && <Alert type='success'>{successMessage}</Alert>}
      {errorMessage && <Alert type='error'>{errorMessage}</Alert>}
      {error && <Alert type='error'>{error}</Alert>}

      <EditDrawer
        carriers={carriers}
        onClose={() => setRecord(null)}
        onSave={handleSave}
        record={record}
        show={!!record}
      />
      <Drawer
        alignRight={true}
        footer={<Button onClick={startImport}>Start Import</Button>}
        onClose={() => setShowImport(false)}
        show={showImport}
        title='Import CSV'
      >
        {importErrors.length ? (
          <Alert type='error'>
            <div style={{ marginBottom: 5 }}>Missing the following columns:</div>
            <ul style={{ paddingLeft: 25, listStyleType: 'disc' }}>
              {importErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </Alert>
        ) : null}
        <div style={{ marginBottom: 40 }}>
          <FileField file={importFile} onChange={handleImportChange} />
        </div>
        <Table
          columns={[
            { id: 'name', label: 'Headers' },
            {
              id: 'required',
              label: 'Required',
              render: row =>
                row.required ? (
                  <i
                    className='mdi mdi-check-bold'
                    style={{ fontSize: 20, color: Colors.GREEN.hex }}
                  />
                ) : null,
              styles: { textAlign: 'center' }
            }
          ]}
          data={[
            { name: 'AMB#', required: true },
            { name: 'AMB Company Name', required: true },
            { name: "Best's Financial Strength Rating - Current", required: false },
            { name: "Best's Financial Strength Rating Effective Date - Current", required: false },
            {
              name: "Best's Financial Strength Rating Outlook/Implication - Current",
              required: false
            },
            { name: 'Financial Size Category', required: false },
            { name: "Best's Long Term Issuer Credit Rating - Current", required: false },
            {
              name: "Best's Long Term Issuer Credit Rating Effective Date - Current",
              required: false
            },
            {
              name: "Best's Long Term Issuer Credit Rating Outlook/Implication - Current",
              required: false
            },
            { name: 'Street Address', required: false },
            { name: 'City', required: false },
            { name: 'State', required: false },
            { name: 'Zip Code', required: false },
            { name: 'Telephone', required: false },
            { name: 'Bonds (%)', required: false },
            { name: 'Mortgage Loans and Real Estate (%)', required: false },
            { name: 'Common and Preferred Stock (%)', required: false },
            { name: 'All Other Assets (%)', required: false },
            { name: 'Total Assets ($000)', required: false },
            { name: 'Life Reserves (%)', required: false },
            { name: 'Health Reserves (%)', required: false },
            { name: 'Annuity Reserves and Deposit Liabilities (%)', required: false },
            { name: 'All Other Liabilities (%)', required: false },
            { name: 'Capital and Surplus ($000)', required: false },
            { name: 'Direct Premiums Written ($000)', required: false },
            { name: 'Net Premiums Written & Deposits ($000)', required: false },
            { name: 'Operating Cash Flow ($000)', required: false },
            { name: 'NOG Before Taxes ($000)', required: false },
            { name: 'NOG After Taxes ($000)', required: false },
            { name: 'Net Income ($000)', required: false },
            { name: 'Unrealized Capital Gains ($000)', required: false },
            { name: 'Benefits Paid to NPW & Dep (%)', required: false },
            { name: 'Commissions and Expenses to NPW & Dep (%)', required: false },
            { name: 'NOG to Total Assets (%)', required: false },
            { name: 'NOG to Total Revenue (%)', required: false },
            { name: 'Operating Return On Equity (%)', required: false },
            { name: 'Net Yield (%)', required: false },
            { name: 'Pre-tax Invest Total Return (%)', required: false },
            { name: 'Change in NPW & Dep (%)', required: false },
            { name: 'NPW & Dep to Total Capital (X)', required: false },
            { name: "Best's Capital Adequacy Ratio", required: false },
            { name: 'Capital and Surplus to Liabilities (%)', required: false },
            { name: 'Surplus Relief (%)', required: false },
            { name: 'Reinsurance Leverage (%)', required: false },
            { name: 'Change in Capital & Surplus (%)', required: false },
            { name: 'Quick Liquidity (%)', required: false },
            { name: 'First Line of Business', required: false },
            { name: 'Second Line of Business', required: false },
            { name: 'Third Line of Business', required: false },
            { name: 'Fourth Line of Business', required: false },
            { name: 'Fifth Line of Business', required: false },
            { name: 'First Line of Business (%)', required: false },
            { name: 'Second Line of Business (%)', required: false },
            { name: 'Third Line of Business (%)', required: false },
            { name: 'Fourth Line of Business (%)', required: false },
            { name: 'Fifth Line of Business (%)', required: false },
            { name: 'First State of Business', required: false },
            { name: 'Second State of Business', required: false },
            { name: 'Third State of Business', required: false },
            { name: 'Fourth State of Business', required: false },
            { name: 'Fifth State of Business', required: false },
            { name: 'First State of Business (%)', required: false },
            { name: 'Second State of Business (%)', required: false },
            { name: 'Third State of Business (%)', required: false },
            { name: 'Fourth State of Business (%)', required: false },
            { name: 'Fifth State of Business (%)', required: false }
          ]}
        />
      </Drawer>

      <H1>AMBest Records</H1>

      <div style={{ display: 'flex', marginBottom: 30 }}>
        <Input
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
          placeholder='Search by carrier name...'
          style={{ width: 300 }}
          value={localSearch}
        />
        <Button
          onClick={() => {
            setShowImport(true);
          }}
          style={{ marginLeft: 'auto' }}
        >
          Import CSV
        </Button>
      </div>

      <Table
        apiPagination={{ ...pagination, current_page: currentPage, onClick: handlePageChange }}
        columns={[
          {
            id: 'ambest_carrier_name',
            is_sortable: true,
            label: 'Name'
          },
          {
            id: 'carrier_id',
            label: 'Assigned Carrier'
          },
          {
            id: 'updated_at',
            is_sortable: true,
            label: 'Last Updated',
            render: record => format(new Date(record.updated_at), 'MMM do, yyyy @ h:mm aaa z'),
            sort_by: record => format(new Date(record.updated_at), 't')
          }
        ]}
        data={ambestRecords}
        defaultSortColumn={{ id: 'ambest_carrier_name' }}
        onRowClick={setRecord}
      />
    </Container>
  );
};

export default List;
